import i18n from 'i18next';
import apiClient from './apiClient.ts';

export class RateLimitError extends Error {
  readonly retryAfterSeconds: number;

  constructor(retryAfterSeconds: number, message: string) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function getRateLimitInfo(error: unknown): { retryAfterSeconds: number } | null {
  if (typeof error !== 'object' || error === null || !('response' in error)) return null;
  const axiosError = error as {
    response?: {
      status?: number;
      data?: { retryAfterSeconds?: number };
      headers?: Record<string, string | number | undefined>;
    };
  };
  if (axiosError.response?.status !== 429) return null;

  const fromBody = axiosError.response.data?.retryAfterSeconds;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody)) {
    return { retryAfterSeconds: Math.max(1, Math.floor(fromBody)) };
  }
  const fromHeader = axiosError.response.headers?.['retry-after'];
  const parsed = typeof fromHeader === 'string' ? parseInt(fromHeader, 10) : Number(fromHeader);
  if (Number.isFinite(parsed) && parsed > 0) {
    return { retryAfterSeconds: parsed };
  }
  return { retryAfterSeconds: 60 };
}

function buildRateLimitMessage(retryAfterSeconds: number): string {
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  if (minutes === 1) {
    return i18n.t('auth.errors.rateLimitedOneMinute', {
      defaultValue: 'Demasiados intentos. Inténtalo de nuevo en 1 minuto.',
    });
  }
  return i18n.t('auth.errors.rateLimitedMinutes', {
    minutes,
    defaultValue: `Demasiados intentos. Inténtalo de nuevo en ${minutes} minutos.`,
  });
}

function toApiError(error: unknown): Error {
  const rateLimit = getRateLimitInfo(error);
  if (rateLimit) {
    return new RateLimitError(
      rateLimit.retryAfterSeconds,
      buildRateLimitMessage(rateLimit.retryAfterSeconds),
    );
  }
  return new Error(extractErrorMessage(error));
}

function extractErrorMessage(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error
  ) {
    const axiosError = error as { response?: { data?: { message?: string; title?: string } } };
    const data = axiosError.response?.data;
    if (data?.message) return data.message;
    if (data?.title) return data.title;
  }
  if (error instanceof Error) return error.message;
  return 'Ha ocurrido un error inesperado';
}

export async function get<R>(url: string): Promise<R> {
  try {
    const response = await apiClient.get<R>(url);
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function post<T, R>(url: string, data: T): Promise<R> {
  try {
    const response = await apiClient.post<R>(url, data);
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function put<T, R>(url: string, data: T): Promise<R> {
  try {
    const response = await apiClient.put<R>(url, data);
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
}

export async function del<R>(url: string): Promise<R> {
  try {
    const response = await apiClient.delete<R>(url);
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
}
